import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, LogOut, Settings2, ShieldCheck, TrendingUp, Clock3 } from 'lucide-react';
import { floorToInt } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { getMainSiteUrl, isAdminUIHostAllowed } from '../lib/siteRouting';
import { api } from '../lib/api';
import useAdminSession from '../hooks/useAdminSession';
import AdminWithdrawalsSection from '../components/AdminWithdrawalsSection';
import AdminUsersSectionV2 from '../components/AdminUsersSectionV2';
import AdminTopupRequestsSection from '../components/AdminTopupRequestsSection';
import AdminTopupUpiIdsSection from '../components/AdminTopupUpiIdsSection';
import AdminSettingsSection from '../components/AdminSettingsSection';

const NotificationBadge = ({ count, testId }) => {
  const parsedCount = Number(count || 0);
  const safeCount = Number.isFinite(parsedCount) ? parsedCount : 0;
  if (safeCount <= 0) return null;

  return (
    <span
      className="inline-flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-red-600 px-1 text-[9px] font-bold leading-none text-white shadow-sm shadow-red-600/25"
      data-testid={testId}
      aria-label={`${safeCount} pending requests`}
    >
      {safeCount}
    </span>
  );
};

const AdminPanel = () => {
  const {
    poolTotal,
    nextDrawAt,
    adminConfig,
    refreshAdminConfig,
    updateAdminPoolConfig,
  } = useAuth();
  const navigate = useNavigate();
  const { adminAccount, adminLoading, adminLogout, updateAdminCredentials } = useAdminSession(isAdminUIHostAllowed());

  const [poolPerc, setPoolPerc] = useState(100);
  const [addToPool, setAddToPool] = useState(0);
  const [autoAddDaily, setAutoAddDaily] = useState(false);
  const [referralIncentivePerc, setReferralIncentivePerc] = useState(10);
  const [freePuzzleSponsorLinks, setFreePuzzleSponsorLinks] = useState(['', '', '']);

  const [saving, setSaving] = useState(false);
  const [configSnapshot, setConfigSnapshot] = useState(null);
  const [infoMessage, setInfoMessage] = useState('');
  const [savedToastVisible, setSavedToastVisible] = useState(false);
  const [activeAdminTab, setActiveAdminTab] = useState('users');
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState('');
  const [pendingWithdrawalCount, setPendingWithdrawalCount] = useState(0);
  const [pendingTopupCount, setPendingTopupCount] = useState(0);

  const [notificationForm, setNotificationForm] = useState({
    title: '', body: '', target: 'all', sendMode: 'now', scheduledAt: '', repeat: 'none', enabled: true,
  });
  const [notificationSaving, setNotificationSaving] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [isNotificationSectionOpen, setIsNotificationSectionOpen] = useState(false);
  const [isSponsorLinksOpen, setIsSponsorLinksOpen] = useState(false);

  useEffect(() => {
    if (!isAdminUIHostAllowed()) {
      navigate('/login?next=/admin', { replace: true });
      return;
    }

    if (!adminLoading && !adminAccount) {
      navigate('/login?next=/admin', { replace: true });
    }
  }, [adminAccount, adminLoading, navigate]);

  useEffect(() => {
    if (adminConfig) {
      setPoolPerc(adminConfig.poolPerc ?? 100);
      setAddToPool(adminConfig.addToPool ?? 0);
      setAutoAddDaily(Boolean(adminConfig.autoAddDaily));
      setReferralIncentivePerc(adminConfig.referralIncentivePerc ?? 10);
      setFreePuzzleSponsorLinks(Array.isArray(adminConfig.freePuzzleSponsorLinks) ? [...adminConfig.freePuzzleSponsorLinks, '', '', ''].slice(0, 3) : [adminConfig.freePuzzleSponsorLink || '', '', '']);
      setConfigSnapshot({
        poolPerc: adminConfig.poolPerc ?? 100,
        addToPool: adminConfig.addToPool ?? 0,
        autoAddDaily: Boolean(adminConfig.autoAddDaily),
        referralIncentivePerc: adminConfig.referralIncentivePerc ?? 10,
        freePuzzleSponsorLinks: Array.isArray(adminConfig.freePuzzleSponsorLinks) ? [...adminConfig.freePuzzleSponsorLinks, '', '', ''].slice(0, 3) : [adminConfig.freePuzzleSponsorLink || '', '', ''],
      });
    }
  }, [adminConfig]);


  useEffect(() => {
    if (adminAccount) {
      loadAdminNotifications();
    }
  }, [adminAccount]);

  useEffect(() => {
    if (!adminAccount) {
      setPendingWithdrawalCount(0);
      setPendingTopupCount(0);
      return undefined;
    }

    let isMounted = true;
    const refreshPendingRequestCounts = async () => {
      try {
        const [withdrawalsResult, topupsResult] = await Promise.all([
          api.getAdminWithdrawals({ status: 'pending' }),
          api.getAdminTopupRequests({ status: 'pending' }),
        ]);

        if (!isMounted) return;
        setPendingWithdrawalCount(Number(withdrawalsResult.data?.stats?.totalPending ?? withdrawalsResult.data?.withdrawals?.length ?? 0));
        setPendingTopupCount(Number(topupsResult.data?.stats?.totalPending ?? topupsResult.data?.requests?.length ?? 0));
      } catch {
        // Keep the last known badge counts if a transient refresh fails.
      }
    };

    refreshPendingRequestCounts();
    const interval = setInterval(refreshPendingRequestCounts, 10000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [adminAccount]);


  const loadAdminNotifications = async () => {
    try {
      const { data } = await api.getAdminNotifications();
      setAdminNotifications(data?.notifications || []);
    } catch {
      // Non-critical history refresh.
    }
  };

  const handleSendNotification = async () => {
    setNotificationSaving(true);
    setNotificationMessage('');
    try {
      const payload = { ...notificationForm };
      if (payload.sendMode === 'scheduled') {
        payload.scheduledAt = notificationForm.scheduledAt ? new Date(notificationForm.scheduledAt).toISOString() : null;
      } else {
        payload.scheduledAt = null;
      }
      const { data } = await api.createAdminNotification(payload);
      const sent = data?.delivery?.sent ?? data?.notification?.sentCount ?? 0;
      const failed = data?.delivery?.failed ?? data?.notification?.failedCount ?? 0;
      setNotificationMessage(payload.sendMode === 'now' ? `Notification sent to ${sent} device(s); ${failed} failed.` : 'Notification scheduled.');
      setNotificationForm((current) => ({ ...current, title: '', body: '' }));
      await loadAdminNotifications();
    } catch (error) {
      setNotificationMessage(error?.response?.data?.detail || error.message || 'Failed to save notification.');
    } finally {
      setNotificationSaving(false);
    }
  };

  const handleToggleNotification = async (notificationId, enabled) => {
    try {
      await api.updateAdminNotificationStatus(notificationId, { enabled });
      await loadAdminNotifications();
    } catch (error) {
      setNotificationMessage(error?.response?.data?.detail || error.message || 'Failed to update notification.');
    }
  };

  const nextDrawLabel = useMemo(() => {
    if (!nextDrawAt) return '--';
    const parsed = new Date(nextDrawAt);
    if (Number.isNaN(parsed.getTime())) return '--';
    return parsed.toLocaleString();
  }, [nextDrawAt]);

  const handleSaveConfig = async () => {
    if (!configSnapshot) {
      return;
    }

    setSaving(true);
    setInfoMessage('');

    const nextConfig = {
      poolPerc: Number(poolPerc),
      addToPool: Number(addToPool),
      autoAddDaily,
      referralIncentivePerc: Number(referralIncentivePerc),
      freePuzzleSponsorLinks: freePuzzleSponsorLinks.map((link) => link.trim()),
    };

    const payload = {};
    Object.entries(nextConfig).forEach(([key, value]) => {
      if (configSnapshot[key] !== value) {
        payload[key] = value;
      }
    });

    if (Object.keys(payload).length === 0) {
      setInfoMessage('No changes to save.');
      setSaving(false);
      return;
    }

    const result = await updateAdminPoolConfig(payload);

    if (result.success) {
      const notificationsSent = result?.config?.notificationsSent || result?.notificationsSent;
      setInfoMessage(
        typeof notificationsSent === 'number' && notificationsSent > 0
          ? `Admin config saved. Broadcast sent to ${notificationsSent} users.`
          : 'Admin configuration saved and applied immediately.'
      );
      await refreshAdminConfig();

      // Show centered "Saved" toast then reload the page so the admin sees a
      // fresh server-rendered state (Live Pool, achievers, etc.).
      setSavedToastVisible(true);
      setSaving(false);
      setTimeout(() => {
        try {
          window.location.reload();
        } catch (_) {
          // no-op
        }
      }, 900);
      return;
    }

    setInfoMessage(result.error || 'Failed to save config');
    setSaving(false);
  };

  const handleBackClick = () => {
    window.location.href = getMainSiteUrl('/dashboard');
  };

  const handleLogout = async () => {
    await adminLogout();
    navigate('/login?next=/admin', { replace: true });
  };

  const handleSaveCredentials = async ({ currentPassword, username, newPassword, resetForm }) => {
    setSettingsSaving(true);
    setSettingsMessage('');
    const result = await updateAdminCredentials({ currentPassword, username, newPassword });
    if (result.success) {
      setSettingsMessage('Admin credentials updated successfully.');
      if (typeof resetForm === 'function') {
        resetForm();
      }
    } else {
      setSettingsMessage(result.error || 'Failed to update admin credentials.');
    }
    setSettingsSaving(false);
  };

  if (adminLoading || !adminAccount) {
    return null;
  }

  return (
    <div className="w-full max-w-md mx-auto min-h-screen bg-white px-5 py-5 space-y-4">
      {savedToastVisible && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center px-6 pointer-events-none"
          role="status"
          aria-live="polite"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: 'spring', stiffness: 320, damping: 22 }}
            className="pointer-events-auto rounded-2xl bg-gradient-to-r from-[#9333EA] to-[#EC4899] text-white px-7 py-4 shadow-2xl flex items-center gap-3"
          >
            <div className="h-7 w-7 rounded-full bg-white/20 flex items-center justify-center">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <span className="text-base font-semibold font-['Outfit']">Saved</span>
          </motion.div>
        </div>
      )}

      <motion.div
        className="glass-panel rounded-[18px] p-4 flex items-center justify-between"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <button
          onClick={handleBackClick}
          className="h-10 w-10 rounded-full bg-white/70 flex items-center justify-center"
        >
          <ArrowLeft className="w-5 h-5 text-[#333333]" />
        </button>
        <div className="text-center">
          <p className="text-sm text-[#666666] font-['DM_Sans']">{adminAccount?.username}</p>
          <h1 className="text-xl font-bold text-[#000000] font-['Outfit']">Admin Panel</h1>
        </div>
        <button
          onClick={handleLogout}
          className="h-10 w-10 rounded-full bg-[#9333EA]/10 flex items-center justify-center"
          title="Logout"
        >
          <LogOut className="w-5 h-5 text-[#9333EA]" />
        </button>
      </motion.div>

      <div className="rounded-[16px] bg-white/80 border border-[#ececec] p-1 grid grid-cols-5 gap-1">
        <button
          onClick={() => setActiveAdminTab('withdrawals')}
          className={`py-2.5 rounded-[12px] text-[11px] font-semibold transition-colors ${
            activeAdminTab === 'withdrawals' ? 'bg-[#9333EA] text-white' : 'text-[#666666] bg-white'
          }`}
          data-testid="admin-tab-withdrawals"
        >
          <span className="inline-flex items-center justify-center gap-0.5 whitespace-nowrap text-[10px] sm:text-[11px]">
            <span>Withdrawals</span>
            <NotificationBadge count={pendingWithdrawalCount} testId="admin-withdrawals-badge" />
          </span>
        </button>
        <button
          onClick={() => setActiveAdminTab('topups')}
          className={`py-2.5 rounded-[12px] text-[11px] font-semibold transition-colors ${
            activeAdminTab === 'topups' ? 'bg-[#9333EA] text-white' : 'text-[#666666] bg-white'
          }`}
          data-testid="admin-tab-topups"
        >
          <span className="inline-flex items-center justify-center gap-0.5 whitespace-nowrap text-[10px] sm:text-[11px]">
            <span>Top-Ups</span>
            <NotificationBadge count={pendingTopupCount} testId="admin-topups-badge" />
          </span>
        </button>
        <button
          onClick={() => setActiveAdminTab('controls')}
          className={`py-2.5 rounded-[12px] text-[11px] font-semibold transition-colors ${
            activeAdminTab === 'controls' ? 'bg-[#9333EA] text-white' : 'text-[#666666] bg-white'
          }`}
          data-testid="admin-tab-controls"
        >
          Controls
        </button>
        <button
          onClick={() => setActiveAdminTab('users')}
          className={`py-2.5 rounded-[12px] text-[11px] font-semibold transition-colors ${
            activeAdminTab === 'users' ? 'bg-[#9333EA] text-white' : 'text-[#666666] bg-white'
          }`}
          data-testid="admin-tab-users"
        >
          Users
        </button>
        <button
          onClick={() => setActiveAdminTab('settings')}
          className={`py-2.5 rounded-[12px] text-[11px] font-semibold transition-colors ${
            activeAdminTab === 'settings' ? 'bg-[#9333EA] text-white' : 'text-[#666666] bg-white'
          }`}
          data-testid="admin-tab-settings"
        >
          Settings
        </button>
      </div>

      {activeAdminTab === 'controls' ? (
        <>
          <motion.div
            className="glass-panel rounded-[18px] p-4 space-y-2"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            <div className="flex items-center gap-2 text-[#666666] text-sm font-['DM_Sans']">
              <TrendingUp className="w-4 h-4" />
              Live Pool
            </div>
            <p className="text-4xl font-bold text-gradient font-['Outfit']">{floorToInt(poolTotal)}</p>
          </motion.div>

          <AdminTopupUpiIdsSection />

          <motion.div
            className="glass-panel rounded-[18px] overflow-hidden"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08 }}
            data-testid="admin-send-notification-section"
          >
            <button
              type="button"
              onClick={() => setIsNotificationSectionOpen((current) => !current)}
              className="w-full flex items-center justify-between gap-3 p-4 text-left"
              aria-expanded={isNotificationSectionOpen}
              aria-controls="admin-send-notification-content"
              data-testid="admin-send-notification-toggle"
            >
              <div>
                <h2 className="text-lg font-bold text-[#111111] font-['Outfit']">Send Notification</h2>
                <p className="text-xs text-[#666666] font-['DM_Sans']">Push notifications reach installed Android APK users with notification permission enabled.</p>
              </div>
              <ChevronDown className={`w-5 h-5 shrink-0 text-[#666666] transition-transform duration-200 ${isNotificationSectionOpen ? 'rotate-180' : ''}`} />
            </button>
            {isNotificationSectionOpen && (
              <motion.div id="admin-send-notification-content" className="border-t border-[#ececec] px-4 pb-4 pt-3 space-y-4" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} transition={{ duration: 0.22, ease: 'easeOut' }}>
                <input className="w-full p-3 rounded-[14px] border border-[#e9e9e9] bg-white" placeholder="Notification title" value={notificationForm.title} onChange={(e) => setNotificationForm((f) => ({ ...f, title: e.target.value }))} />
                <textarea className="w-full p-3 rounded-[14px] border border-[#e9e9e9] bg-white" rows={3} placeholder="Notification message/body" value={notificationForm.body} onChange={(e) => setNotificationForm((f) => ({ ...f, body: e.target.value }))} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <select className="p-3 rounded-[14px] border border-[#e9e9e9] bg-white" value={notificationForm.target} onChange={(e) => setNotificationForm((f) => ({ ...f, target: e.target.value }))}>
                    <option value="all">All users</option><option value="active">Only active users</option><option value="subscribed">Subscribed users</option>
                  </select>
                  <select className="p-3 rounded-[14px] border border-[#e9e9e9] bg-white" value={notificationForm.sendMode} onChange={(e) => setNotificationForm((f) => ({ ...f, sendMode: e.target.value }))}>
                    <option value="now">Send now</option><option value="scheduled">Schedule for later date/time</option>
                  </select>
                  {notificationForm.sendMode === 'scheduled' && <input type="datetime-local" className="p-3 rounded-[14px] border border-[#e9e9e9] bg-white" value={notificationForm.scheduledAt} onChange={(e) => setNotificationForm((f) => ({ ...f, scheduledAt: e.target.value }))} />}
                  <select className="p-3 rounded-[14px] border border-[#e9e9e9] bg-white" value={notificationForm.repeat} onChange={(e) => setNotificationForm((f) => ({ ...f, repeat: e.target.value }))}>
                    <option value="none">No repeat</option><option value="daily">Daily at same time</option><option value="weekly">Weekly at same time</option>
                  </select>
                </div>
                <label className="flex items-center justify-between p-3 rounded-[14px] bg-white/70 border border-[#ececec]"><span className="text-sm">Enable scheduled notification</span><input type="checkbox" checked={notificationForm.enabled} onChange={(e) => setNotificationForm((f) => ({ ...f, enabled: e.target.checked }))} /></label>
                <button onClick={handleSendNotification} disabled={notificationSaving || !notificationForm.title.trim() || !notificationForm.body.trim()} className="w-full py-3 rounded-full bg-gradient-to-r from-[#9333EA] to-[#EC4899] text-white font-semibold disabled:opacity-60">{notificationSaving ? 'Saving...' : (notificationForm.sendMode === 'now' ? 'Send' : 'Save')}</button>
                {notificationMessage && <p className="text-xs text-[#333333] bg-white/70 rounded-[12px] p-2">{notificationMessage}</p>}
                <div className="space-y-2">{adminNotifications.slice(0, 5).map((item) => <div key={item.id} className="p-3 rounded-[12px] bg-white/70 border border-[#ececec] text-xs"><div className="font-semibold">{item.title} · {item.status}</div><div>{item.body}</div><button className="mt-2 text-[#9333EA] font-semibold" onClick={() => handleToggleNotification(item.id, !item.enabled)}>{item.enabled ? 'Disable' : 'Enable'}</button></div>)}</div>
              </motion.div>
            )}
          </motion.div>


          <motion.div
            className="glass-panel rounded-[18px] p-4 space-y-4"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div>
              <label className="block text-sm text-[#666666] mb-1 font-['DM_Sans']">Pool perc (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                value={poolPerc}
                onChange={(e) => setPoolPerc(e.target.value)}
                className="w-full p-3 rounded-[14px] border border-[#e9e9e9] bg-white"
              />
            </div>

            <div>
              <label className="block text-sm text-[#666666] mb-1 font-['DM_Sans']">Add to Pool (fixed number)</label>
              <input
                type="number"
                min="0"
                value={addToPool}
                onChange={(e) => setAddToPool(e.target.value)}
                className="w-full p-3 rounded-[14px] border border-[#e9e9e9] bg-white"
              />
            </div>

            <label className="flex items-center justify-between p-3 rounded-[14px] bg-white/70 border border-[#ececec]">
              <span className="text-sm text-[#111111] font-['DM_Sans']">Auto add daily</span>
              <input
                type="checkbox"
                checked={autoAddDaily}
                onChange={(e) => setAutoAddDaily(e.target.checked)}
                className="w-5 h-5"
              />
            </label>

            <div>
              <label className="block text-sm text-[#666666] mb-1 font-['DM_Sans']">Referral Incentive %</label>
              <input
                type="number"
                min="0"
                max="100"
                value={referralIncentivePerc}
                onChange={(e) => setReferralIncentivePerc(e.target.value)}
                className="w-full p-3 rounded-[14px] border border-[#e9e9e9] bg-white"
              />
            </div>

            <div className="rounded-[14px] border border-[#ececec] bg-white/80 overflow-hidden">
              <button
                type="button"
                onClick={() => setIsSponsorLinksOpen((current) => !current)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
                aria-expanded={isSponsorLinksOpen}
                aria-controls="admin-sponsor-links-content"
                data-testid="admin-sponsor-links-toggle"
              >
                <div>
                  <p className="text-sm font-semibold text-[#000000] font-['Outfit']">Sponsor Links</p>
                  <p className="text-xs text-[#666666] font-['DM_Sans']">Puzzle sponsor links</p>
                </div>
                <ChevronDown className={`w-4 h-4 text-[#666666] transition-transform duration-200 ${isSponsorLinksOpen ? 'rotate-180' : ''}`} />
              </button>
              {isSponsorLinksOpen && (
                <motion.div
                  id="admin-sponsor-links-content"
                  className="border-t border-[#ececec] px-4 py-3 space-y-3 bg-white/60"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  transition={{ duration: 0.22, ease: 'easeOut' }}
                >
                  {[1, 2, 3].map((entryNumber) => (
                    <div key={entryNumber}>
                      <label className="block text-sm text-[#666666] mb-1 font-['DM_Sans']">
                        {entryNumber === 1 ? 'Puzzle 1 Sponsor Link' : `Puzzle ${entryNumber} Sponsor Link`}
                      </label>
                      <input
                        type="url"
                        value={freePuzzleSponsorLinks[entryNumber - 1] || ''}
                        onChange={(e) => setFreePuzzleSponsorLinks((current) => {
                          const next = [...current, '', '', ''].slice(0, 3);
                          next[entryNumber - 1] = e.target.value;
                          return next;
                        })}
                        className="w-full p-3 rounded-[14px] border border-[#e9e9e9] bg-white"
                        placeholder="Paste monetized sponsor/short link"
                        data-testid={`admin-free-puzzle-sponsor-link-${entryNumber}`}
                      />
                    </div>
                  ))}
                  <p className="mt-1 text-xs text-[#777777] font-['DM_Sans']">Set each short link's final destination to https://harday.co.in/free-puzzle.</p>
                </motion.div>
              )}
            </div>

            <div>
              <button
                onClick={handleSaveConfig}
                disabled={saving}
                className="w-full py-3 rounded-full bg-gradient-to-r from-[#9333EA] to-[#EC4899] text-white font-semibold disabled:opacity-60"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>

            {infoMessage && (
              <p className="text-xs text-[#333333] font-['DM_Sans'] bg-white/70 rounded-[12px] p-2">
                {infoMessage}
              </p>
            )}
          </motion.div>
        </>
      ) : activeAdminTab === 'users' ? (
        <AdminUsersSectionV2 />
      ) : activeAdminTab === 'topups' ? (
        <AdminTopupRequestsSection />
      ) : activeAdminTab === 'settings' ? (
        <AdminSettingsSection
          adminUsername={adminAccount?.username || ''}
          onSaveCredentials={handleSaveCredentials}
          isSaving={settingsSaving}
          infoMessage={settingsMessage}
        />
      ) : (
        <AdminWithdrawalsSection />
      )}

      {activeAdminTab === 'settings' && (
        <div className="glass-panel rounded-[18px] p-4 text-xs text-[#666666] font-['DM_Sans'] flex items-center gap-2">
          <Settings2 className="w-4 h-4 text-[#9333EA]" />
          Sessions stay active for 24 hours. Updating credentials securely rotates your admin session.
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
